import React from "react";
import { FaExternalLinkAlt, FaShareAlt } from "react-icons/fa";
import { Badge } from "./common";
import { getSocialPlatformConfig } from "../../../components/SocialPlatformIcon";
import { socialPlatformLabel } from "../../../constants/socialPlatforms";
import { fmtDate, listToText, locationsToText } from "../dmApi";

const show = (v) => (v == null || v === "" ? <span className="dmp-muted">—</span> : v);

/** Client details, mail / social accounts and the project brief. */
function TenantOverview({ overview, project, onManageAccounts, showBrief = true, showDetails = true, showAccounts = true }) {
  const accounts = overview?.Social_Accounts || [];
  return (
    <>
      {showDetails ? (
      <div className="dmp-card">
        <h3 className="dmp-card__title">Client details</h3>
        <dl className="dmp-kv">
          <div>
            <dt>Client name</dt>
            <dd>{show(overview?.Client_Name)}</dd>
          </div>
          <div>
            <dt>Business name</dt>
            <dd>{show(overview?.Business_Name)}</dd>
          </div>
          <div>
            <dt>Business type</dt>
            <dd>{show(overview?.Business_Type)}</dd>
          </div>
          <div>
            <dt>Website</dt>
            <dd>
              {overview?.Website ? (
                <a href={/^https?:/i.test(overview.Website) ? overview.Website : `https://${overview.Website}`} target="_blank" rel="noreferrer">
                  {overview.Website}
                </a>
              ) : (
                show(null)
              )}
            </dd>
          </div>
          <div>
            <dt>Mobile</dt>
            <dd>{show(overview?.Mobile)}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{show(overview?.Email)}</dd>
          </div>
          <div>
            <dt>Digital Marketing services</dt>
            <dd className="dmp-chips">
              {(overview?.Dm_Access?.services || []).length
                ? overview.Dm_Access.services.map((s) => (
                    <Badge key={s} value="Organic">
                      {s}
                    </Badge>
                  ))
                : show(null)}
            </dd>
          </div>
        </dl>
      </div>
      ) : null}

      {showAccounts ? (
      <div className="dmp-card">
        <h3 className="dmp-card__title">
          Social Accounts
          {onManageAccounts ? (
            <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={onManageAccounts}>
              Manage accounts
            </button>
          ) : null}
        </h3>
        {accounts.length === 0 ? (
          <div className="dmp-empty">No accounts added yet (Gmail, GMB, Facebook, Instagram, LinkedIn, YouTube, WA Business).</div>
        ) : (
          <div className="dmp-accounts">
            {accounts.map((a) => {
              const config = getSocialPlatformConfig(a.Platform_Name);
              const PlatformIcon = config?.Icon || FaShareAlt;
              const login = a.Email_Used || a.Username;
              const profileUrl = a.Profile_URL || a.Business_URL;
              return (
                <div key={a.Account_Id} className="dmp-account">
                  <span
                    className="dmp-account__icon"
                    style={{ background: config?.color || "#64748b" }}
                    aria-hidden="true"
                  >
                    <PlatformIcon />
                  </span>
                  <div className="dmp-account__body">
                    <div className="dmp-account__top">
                      <strong className="dmp-account__name">{a.Account_Name || socialPlatformLabel(a.Platform_Name)}</strong>
                      <Badge value={a.Status} />
                    </div>
                    <div className="dmp-account__platform">{socialPlatformLabel(a.Platform_Name) || a.Platform_Name}</div>
                    {login ? <div className="dmp-account__login">{login}</div> : null}
                  </div>
                  {profileUrl ? (
                    <a
                      className="dmp-account__open"
                      href={profileUrl}
                      target="_blank"
                      rel="noreferrer"
                      title="Open profile"
                      aria-label="Open profile"
                    >
                      <FaExternalLinkAlt aria-hidden="true" />
                    </a>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
      ) : null}

      {showBrief ? (
      <div className="dmp-card">
        <h3 className="dmp-card__title">Project brief</h3>
        <dl className="dmp-kv">
          <div>
            <dt>Linked client project</dt>
            <dd>{show(project?.Tenant_Project_Name)}</dd>
          </div>
          <div>
            <dt>Location</dt>
            <dd>{show(project?.Location)}</dd>
          </div>
          <div>
            <dt>Dates</dt>
            <dd>
              {project?.Start_Date || project?.End_Date
                ? `${fmtDate(project.Start_Date)} → ${fmtDate(project.End_Date)}`
                : show(null)}
            </dd>
          </div>
          <div>
            <dt>Assigned / prepared / reviewed</dt>
            <dd>
              {[project?.Assigned_To_Name, project?.Prepared_By_Name, project?.Reviewed_By_Name]
                .map((n) => n || "—")
                .join(" / ")}
            </dd>
          </div>
          <div>
            <dt>Keywords</dt>
            <dd>{show(listToText(project?.Keywords))}</dd>
          </div>
          <div>
            <dt>Target locations</dt>
            <dd>{show(locationsToText(project?.Target_Locations))}</dd>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <dt>Description</dt>
            <dd style={{ whiteSpace: "pre-wrap" }}>{show(project?.Description)}</dd>
          </div>
        </dl>
      </div>
      ) : null}
    </>
  );
}

export default TenantOverview;
